<template>
  <div class="container-fluid px-4 mt-4">
    <h5 class="fw-bold mb-4">통합 설정</h5>

    <!-- SMS 설정 카드 -->
    <div class="card mb-4 ef-card">
      <div class="card-header ef-card-header text-white">
        <h6 class="mb-0">📱 Ebpp (문자 서비스) 설정</h6>
      </div>
      <div class="card-body ef-card-body">
        <div v-if="loading" class="text-center text-muted py-5">
          로드 중...
        </div>

        <!-- SMS 설정이 없을 때: 추가 폼 -->
        <div v-else-if="!smsSetting.exists" class="row g-3">
          <p class="text-muted small mb-3">아직 SMS 설정이 없습니다. 아래에서 설정을 추가하세요.</p>
          <div class="col-12">
            <form @submit.prevent="addSmsSetting">
              <div class="mb-3">
                <label for="mid" class="form-label">MID <span class="text-danger">*</span></label>
                <input
                  id="mid"
                  v-model="form.mid"
                  type="text"
                  class="form-control"
                  placeholder="Ebpp MID 입력"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="apiKey" class="form-label">API Key <span class="text-danger">*</span></label>
                <input
                  id="apiKey"
                  v-model="form.apiKey"
                  type="text"
                  class="form-control"
                  placeholder="Ebpp API Key 입력"
                  required
                />
              </div>
              <div v-if="error" class="alert alert-danger py-2 small mb-3">{{ error }}</div>
              <button
                type="submit"
                class="btn ef-btn-gradient"
                :disabled="saving"
              >
                {{ saving ? '저장 중...' : '✅ 추가' }}
              </button>
            </form>
          </div>
        </div>

        <!-- SMS 설정이 있을 때: 수정/삭제 폼 -->
        <div v-else class="row g-3">
          <div class="col-12">
            <div class="bg-light p-3 rounded mb-3">
              <p class="mb-2"><strong>현재 설정:</strong></p>
              <p class="text-muted small mb-1">
                <strong>MID:</strong> {{ smsSetting.data.MID }}
              </p>
              <p class="text-muted small mb-0">
                <strong>API Key:</strong> <span class="font-monospace">{{ maskApiKey(smsSetting.data.ApiKey) }}</span>
              </p>
            </div>
          </div>

          <div class="col-12">
            <form @submit.prevent="updateSmsSetting">
              <div class="mb-3">
                <label for="editMid" class="form-label">MID <span class="text-danger">*</span></label>
                <input
                  id="editMid"
                  v-model="form.mid"
                  type="text"
                  class="form-control"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="editApiKey" class="form-label">API Key <span class="text-danger">*</span></label>
                <input
                  id="editApiKey"
                  v-model="form.apiKey"
                  type="text"
                  class="form-control"
                  required
                />
              </div>
              <div v-if="error" class="alert alert-danger py-2 small mb-3">{{ error }}</div>
              <div class="d-flex gap-2">
                <button
                  type="submit"
                  class="btn ef-btn-primary"
                  :disabled="saving"
                >
                  {{ saving ? '저장 중...' : '✏️ 수정' }}
                </button>
                <button
                  type="button"
                  class="btn ef-btn-danger"
                  @click="openDeleteModal"
                  :disabled="saving"
                >
                  🗑️ 삭제
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- 통합 설정 카드 -->
    <div class="card mb-4 ef-card">
      <div class="card-header ef-card-header text-white">
        <h6 class="mb-0">⚙️ 메뉴 표시 설정</h6>
      </div>
      <div class="card-body ef-card-body">
        <div v-if="commonConfigLoading" class="text-center text-muted py-5">
          로드 중...
        </div>

        <div v-else class="row g-3">
          <div class="col-12">
            <div class="form-check form-switch">
              <input
                id="isVisibleTicketResult"
                type="checkbox"
                class="form-check-input"
                :checked="commonConfig.IsVisibleTicketResult === 1"
                @change="commonConfig.IsVisibleTicketResult = $event.target.checked ? 1 : 0"
              />
              <label for="isVisibleTicketResult" class="form-check-label">
                <strong>추첨 결과 메뉴 표시</strong>
                <small class="text-muted d-block">사용자의 내비게이션 바에서 "추첨 결과" 메뉴의 표시 여부를 설정합니다.</small>
              </label>
            </div>
          </div>

          <div class="col-12">
            <div v-if="commonConfigError" class="alert alert-danger py-2 small mb-3">{{ commonConfigError }}</div>
            <button
              type="button"
              class="btn ef-btn-gradient"
              @click="updateCommonConfig"
              :disabled="commonConfigSaving"
            >
              {{ commonConfigSaving ? '저장 중...' : '✅ 저장' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 삭제 확인 모달 -->
    <div v-if="deleteModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 420px;" @click.stop>
        <h6 class="fw-bold mb-3">SMS 설정 삭제</h6>
        <p class="text-muted mb-3">
          SMS 설정을 삭제하시겠습니까?<br/>
          <span class="text-danger small">이 작업은 되돌릴 수 없습니다.</span>
        </p>
        <div v-if="deleteError" class="alert alert-danger py-1 small mb-2">{{ deleteError }}</div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="deleteModal = false">취소</button>
          <button class="btn ef-btn-danger btn-sm" :disabled="saving" @click="deleteSmsSetting">
            {{ saving ? '삭제 중...' : '삭제' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminCommonSetting',
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      deleteError: null,
      deleteModal: false,
      commonConfigLoading: true,
      commonConfigError: null,
      commonConfigSaving: false,
      commonConfig: {
        ConfigSeq: 1,
        IsVisibleTicketResult: 1,
        IsVisibleParkingReg: 1
      },
      smsSetting: {
        exists: false,
        data: {
          MID: '',
          ApiKey: ''
        }
      },
      form: {
        mid: '',
        apiKey: ''
      }
    }
  },
  mounted() {
    this.fetchSmsSetting()
    this.fetchCommonConfig()
  },
  methods: {
    async fetchSmsSetting() {
      this.loading = true
      this.error = null
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/sms-setting`)
        this.smsSetting = res.data
        if (res.data.exists && res.data.data) {
          this.form.mid = res.data.data.MID
          this.form.apiKey = res.data.data.ApiKey
        }
      } catch (e) {
        console.error('SMS setting fetch error:', e)
        this.error = '설정 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async addSmsSetting() {
      if (!this.form.mid || !this.form.apiKey) {
        this.error = 'MID와 API Key를 모두 입력하세요.'
        return
      }
      this.saving = true
      this.error = null
      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/sms-setting`, {
          mid: this.form.mid,
          apiKey: this.form.apiKey
        })
        await this.fetchSmsSetting()
        this.form.mid = ''
        this.form.apiKey = ''
      } catch (e) {
        console.error('SMS setting add error:', e)
        this.error = e.response?.data?.message || 'SMS 설정 추가에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    async updateSmsSetting() {
      if (!this.form.mid || !this.form.apiKey) {
        this.error = 'MID와 API Key를 모두 입력하세요.'
        return
      }
      this.saving = true
      this.error = null
      try {
        await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/sms-setting`, {
          mid: this.form.mid,
          apiKey: this.form.apiKey
        })
        await this.fetchSmsSetting()
      } catch (e) {
        console.error('SMS setting update error:', e)
        this.error = e.response?.data?.message || 'SMS 설정 수정에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    openDeleteModal() {
      this.deleteModal = true
      this.deleteError = null
    },
    async deleteSmsSetting() {
      this.saving = true
      this.deleteError = null
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/sms-setting`)
        this.deleteModal = false
        await this.fetchSmsSetting()
        this.form.mid = ''
        this.form.apiKey = ''
      } catch (e) {
        console.error('SMS setting delete error:', e)
        this.deleteError = e.response?.data?.message || 'SMS 설정 삭제에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    maskApiKey(apiKey) {
      if (!apiKey || apiKey.length < 8) return '****'
      return `${apiKey.substring(0, 4)}****${apiKey.substring(apiKey.length - 4)}`
    },
    async fetchCommonConfig() {
      this.commonConfigLoading = true
      this.commonConfigError = null
      console.log('[fetchCommonConfig] 시작...')
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/common-config`)
        console.log('[fetchCommonConfig] API 응답:', res.data)
        if (res.data.success && res.data.config) {
          console.log('[fetchCommonConfig] 로드된 config:', res.data.config)
          console.log('[fetchCommonConfig] IsVisibleTicketResult:', res.data.config.IsVisibleTicketResult)
          // 응답이 boolean(true/false)로 올 수 있으므로 1/0으로 정규화
          const cfg = Object.assign({}, res.data.config)
          cfg.IsVisibleTicketResult = cfg.IsVisibleTicketResult ? 1 : 0
          cfg.IsVisibleParkingReg = cfg.IsVisibleParkingReg ? 1 : 0
          this.commonConfig = cfg
          console.log('[fetchCommonConfig] this.commonConfig 업데이트 후:', this.commonConfig)
        } else {
          console.warn('[fetchCommonConfig] 성공했지만 config가 없음:', res.data)
        }
      } catch (e) {
        console.error('[fetchCommonConfig] 오류:', e)
        console.error('[fetchCommonConfig] 상세 오류:', e.response?.data)
        this.commonConfigError = '설정 조회에 실패했습니다.'
      } finally {
        this.commonConfigLoading = false
        console.log('[fetchCommonConfig] 완료. commonConfig:', this.commonConfig)
      }
    },
    async updateCommonConfig() {
      this.commonConfigSaving = true
      this.commonConfigError = null
      console.log('[updateCommonConfig] 저장 시작. commonConfig:', this.commonConfig)
      try {
        const payload = {
          IsVisibleTicketResult: this.commonConfig.IsVisibleTicketResult,
          IsVisibleParkingReg: this.commonConfig.IsVisibleParkingReg
        }
        console.log('[updateCommonConfig] 전송 데이터:', payload)
        const res = await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/common-config`, payload)
        console.log('[updateCommonConfig] 저장 응답:', res.data)
        alert('설정이 저장되었습니다.')
      } catch (e) {
        console.error('[updateCommonConfig] 오류:', e)
        console.error('[updateCommonConfig] 상세 오류:', e.response?.data)
        this.commonConfigError = e.response?.data?.message || '설정 저장에 실패했습니다.'
      } finally {
        this.commonConfigSaving = false
      }
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  min-width: 300px;
}

.ef-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: none;
  border-radius: 8px;
  overflow: hidden;
}

.ef-card-header {
  background: linear-gradient(135deg, #1a1a2e 0%, #3a0a0a 100%);
  border-bottom: none;
  padding: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.ef-card-body {
  padding: 1.5rem;
  background: #f8f9fa;
}

.ef-btn-gradient {
  background: linear-gradient(135deg, #1a1a2e 0%, #3a0a0a 100%);
  color: #ffffff;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.ef-btn-gradient:hover:not(:disabled) {
  background: linear-gradient(135deg, #25253b 0%, #4b1111 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
}

.ef-btn-gradient:disabled {
  background: #666;
  cursor: not-allowed;
}

.ef-btn-primary {
  background: #e63946;
  color: white;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.ef-btn-primary:hover:not(:disabled) {
  background: #d62828;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(230, 57, 70, 0.3);
}

.ef-btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.ef-btn-danger {
  background: #d62828;
  color: white;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.ef-btn-danger:hover:not(:disabled) {
  background: #bb0000;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(214, 40, 40, 0.3);
}

.ef-btn-danger:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.font-monospace {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.form-label {
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
}

.form-control {
  border-color: #ddd;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  border-color: #e63946;
  box-shadow: 0 0 0 0.2rem rgba(230, 57, 70, 0.25);
}

.bg-light {
  background-color: #fff !important;
  border: 1px solid #e0e0e0;
}

.text-danger {
  color: #e63946 !important;
}
</style>
